/**
 * Healthcheck workspace — the tenant's monitoring root.
 *
 * `useWorkspace()` is the GATE the module mounts behind: one `GET /workspace`
 * decides whether to render the app (workspace exists) or the onboarding CTA
 * (`result === null` → not provisioned yet). `useActivateWorkspace()` is the
 * user-driven get-or-create (`POST /workspace`); on success it invalidates the
 * gate + the rest of the healthcheck cache so the gate flips and screens recover.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { healthcheckApi, hcKeys, unwrap, hcExtractError, type ObjectEnvelope } from './_shared';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  organization_id: string;
}

/**
 * GATE query — `GET /workspace`. Returns the workspace, or `null` when the org is
 * resolvable but not provisioned yet (onboarding). Reads `result` directly so a
 * `null` result is preserved (not swallowed by `unwrap`'s body fallback). Non-2xx
 * (401/403/5xx) surfaces as a real error.
 */
export function useWorkspace() {
  return useQuery<Workspace | null>({
    queryKey: hcKeys.workspace,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await healthcheckApi.get<ObjectEnvelope<Workspace | null>>('/workspace');
      return res.data?.result ?? null;
    },
  });
}

export function useActivateWorkspace(
  options?: UseMutationOptions<Workspace, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation<Workspace, Error, void>({
    mutationFn: async () => {
      try {
        return unwrap<Workspace>(await healthcheckApi.post('/workspace'));
      } catch (e) {
        throw new Error(hcExtractError(e, 'Could not create the workspace.'));
      }
    },
    ...options,
    onSuccess: (...args) => {
      // Flip the gate first, then refresh anything that was blocked by the missing
      // tenant (whole hc cache) plus the partner project list gating the picker.
      qc.invalidateQueries({ queryKey: hcKeys.workspace });
      qc.invalidateQueries({ queryKey: hcKeys.all });
      qc.invalidateQueries({ queryKey: ['v3', 'me', 'projects'] });
      options?.onSuccess?.(...args);
    },
  });
}
